import React, { ReactElement, useEffect } from 'react';
import { Menu as AntMenu } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';

interface ClickEvent<T> {
  key: T;
}

interface CustomStyle {
  fontSizeParent?: number;
  marginSubmenuTitle?: CSSSize | 0;
  borderBottomColor?: ColorHex;
  submenuTop?: CSSSize | 0;
}

interface MenuProps<ItemKey extends string> {
  items: Item<ItemKey>[];
  current?: ItemKey;
  onClick: (event: ClickEvent<ItemKey>) => void;
  className?: string;
  firstItemNoPadding?: boolean;
  style?: { [x: string]: unknown };
  customStyle?: CustomStyle;
}

function Menu<ItemKey extends string>({
  items,
  current,
  onClick,
  className,
  firstItemNoPadding,
  style,
  customStyle,
}: MenuProps<ItemKey>): ReactElement {
  const antOnClick = e => {
    onClick && onClick(e);
  };

  const filterVisibleItems = (items: Item<ItemKey>[]): Item<ItemKey>[] => {
    return items.reduce((result, item) => {
      if ('visible' in item && item.visible === false) {
        return result;
      } else if ('children' in item) {
        const filteredChildren = filterVisibleItems(item.children);
        delete item.visible;
        if (filteredChildren.length > 0) {
          result.push({ ...item, children: filteredChildren });
        }
      } else {
        delete item.visible;
        result.push(item);
      }
      return result;
    }, [] as Item<ItemKey>[]);
  };

  const currentStyle = style || { flex: 'auto' };

  return (
    <StyledMenu
      items={filterVisibleItems(items)}
      onClick={antOnClick}
      selectedKeys={current ? [current] : []}
      mode={'horizontal'}
      disabledOverflow={true}
      style={currentStyle}
      customstyle={customStyle}
      className={`${className} ${firstItemNoPadding ? 'menu-first-item-no-padding' : ''}`}
    />
  );
}

type Item<ItemKey> = PlainItem<ItemKey> | GroupItem<ItemKey> | ParentItem<ItemKey> | DividerItem;

interface DividerItem {
  type: 'divider';
  visible?: never;
}

interface PlainItem<ItemKey> {
  label: string | React.ReactNode;
  icon?: React.ReactNode;
  visible?: boolean;
  key: ItemKey;
}

interface ParentItem<ItemKey> {
  label: string | React.ReactNode;
  icon?: React.ReactNode;
  visible?: boolean;
  key: ItemKey | string;
  children: Item<ItemKey>[];
}

interface GroupItem<ItemKey> {
  type: 'group';
  icon?: React.ReactNode;
  visible?: boolean;
  label: string;
  key: ItemKey;
  children: Item<ItemKey>[];
}

const StyledMenu = styled(AntMenu)<{ customstyle?: CustomStyle }>`
  user-select: none;

  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-item:hover,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-submenu:hover,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-item-active,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-item-open,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-submenu-open,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-item-selected,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-submenu-selected {
    color: ${props => /*props.theme.menu.itemColor ??*/ colorsPalette.dustyBlue500};
  }

  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-item:hover::after,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-submenu:hover::after,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-item-active::after,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-item-open::after,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-submenu-open::after,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-item-selected::after,
  &.ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-submenu-selected::after {
    border-bottom: 2px solid
      ${props => /*props.theme.menu.itemColor ??*/ colorsPalette.dustyBlue500};
  }

  &.ant-menu-horizontal:not(.ant-menu-dark),
  > .ant-menu-submenu-active:not(.ant-menu-submenu-selected)::after {
    border-bottom: 0;
  }

  &.ant-menu-light .ant-menu-submenu-selected > .ant-menu-submenu-title {
    color: ${props => /*props.theme.menu.itemColor ??*/ colorsPalette.dustyBlue500};
  }

  :where(.css-dev-only-do-not-override-ixblex).ant-menu-light,
  .ant-menu-submenu-title:hover:not(.ant-menu-item-selected):not(.ant-menu-submenu-selected) {
    color: ${props => /*props.theme.menu.itemColor ??*/ colorsPalette.dustyBlue500};
  }

  &.ant-menu-horizontal .ant-menu-item,
  &.ant-menu-horizontal .ant-menu-submenu {
    padding-inline: 20px;
  }

  &.menu-first-item-no-padding.ant-menu-horizontal {
    border-bottom: 0;

    .ant-menu-item {
      padding-left: 0;

      &:after {
        left: 0;
      }
    }
  }

  &.ant-menu {
    border-bottom: 1px solid ${props => props.customstyle?.borderBottomColor || 'transparent'} !important;
  }

  &.ant-menu .ant-menu-submenu-title .anticon + span {
    margin-inline-start: ${props => props.customstyle?.marginSubmenuTitle};
  }

  &.ant-menu .ant-menu-submenu-title .anticon {
    font-size: ${props =>
      props.customstyle?.fontSizeParent ? `${props.customstyle?.fontSizeParent}px` : undefined};
  }

  &.ant-menu-horizontal > .ant-menu-submenu {
    top: ${props => props.customstyle?.submenuTop ?? undefined};
  }
`;

export { type Item, Menu };
